import { useAction, useAtom } from "@reatom/npm-react";
import { useEffect, useMemo, useRef } from "react";
import { type ParamParseKey, useParams } from "react-router-dom";
import { PageMeta } from "src/components";
import { stripMarkdown } from "src/components/PageMeta/stripMarkdown";
import { appConfig } from "src/config";
import { NotFound } from "src/pages/ErrorBoundary/widgets/NotFound";

import {
  KnowledgeDropdownToc,
  KnowledgeMarkdown,
  KnowledgeToc,
} from "./components";
import { ARTICLE_ROUTE } from "./constants";
import styles from "./KnowledgeBaseLayout.module.scss";
import { knowledgePage } from "./store";
import { IKBPage } from "./types";
import { useKnowledgeBaseOutletContext } from "./useKnowledgeBaseOutletContext";
import { hasKBArticle } from "./utils";

const DESCRIPTION_LIMIT = 160;

export const KnowledgeBaseArticle = () => {
  const { basePath, route, tree, itemsToc, activeId, onActiveChange } =
    useKnowledgeBaseOutletContext();
  const { sectionSlug = "", docSlug = "" } =
    useParams<ParamParseKey<typeof ARTICLE_ROUTE>>();

  const frameRef = useRef<HTMLDivElement>(null);
  const loadPage = useAction(knowledgePage);
  const [pageData] = useAtom(knowledgePage.dataAtom);

  const isKnownArticle = useMemo(
    () => hasKBArticle(tree, sectionSlug, docSlug),
    [tree, sectionSlug, docSlug],
  );

  useEffect(() => {
    if (!isKnownArticle) return;

    loadPage(docSlug);
  }, [isKnownArticle, docSlug, loadPage]);

  if (!isKnownArticle) {
    return <NotFound />;
  }

  const article: IKBPage | null = pageData?.slug === docSlug ? pageData : null;

  if (!article) {
    return null;
  }

  const description = stripMarkdown(article.content).slice(
    0,
    DESCRIPTION_LIMIT,
  );

  return (
    <>
      {route === "base" && (
        <PageMeta
          title={`${article.title} — База знаний | ${appConfig.displayName}`}
          description={description}
          path={`${basePath}/${sectionSlug}/${docSlug}`}
        />
      )}

      <div className={styles.content} ref={frameRef}>
        {route === "lk" && (
          <div className={styles.tocMobile}>
            <KnowledgeDropdownToc activeId={activeId} items={itemsToc} />
          </div>
        )}

        <KnowledgeMarkdown
          framerRef={frameRef}
          basePath={basePath}
          content={article.content}
          onActiveChange={onActiveChange}
          route={route}
        />
      </div>

      <div className={styles.tocPc}>
        <KnowledgeToc items={itemsToc} activeId={activeId} route={route} />
      </div>
    </>
  );
};
